import React, { useState, useMemo, useEffect, useRef } from "react";
import "./SearchDeals.css";
import { Badge, Button } from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";
import { useGetDeals } from "api/deals/service";
import { useNavigate } from "react-router-dom";
import { formatDateForDisplay } from "../../utils/dateUtils";
import { resolveDealListReceivedDate } from "../../utils/dealListDates";
import {
  DEFAULT_DYNAMIC_DATE_RANGE,
  resolveDynamicDateRange,
} from "../../utils/dynamicDateRange";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import SearchDealsFilters from "./components/SearchDealsFilters";
import SearchDealsCards from "./components/SearchDealsCards";
import InventoryStatusTicks, {
  useFilterStatusCounts,
} from "views/inventory/components/InventoryStatusTicks";
import {
  readListState,
  saveListState,
  useClearListStateOnLeave,
  LIST_STATE_FLOWS,
} from "utils/listStateSessionStorage";

const formatDisplayText = (text) => {
  if (!text) return "";
  return text
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const DEALS_LIST_STATE_KEY = "dealertiq-deals-list-state";

const SearchDeals = ({ activeColor = "blue", shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useClearListStateOnLeave(DEALS_LIST_STATE_KEY, LIST_STATE_FLOWS.deals);

  const statusOptions = useMemo(
    () => [
      { value: "working", label: t("searchDeals.working"), color: "#0092e0" },
      { value: "prospect", label: t("searchDeals.prospect"), color: "#16A34A" },
      { value: "contracted", label: "Contracted", color: "#b98f06" },
      { value: "spot_delivered", label: "Spot Delivered", color: "#9d6413" },
      { value: "cancelled", label: "Cancelled", color: "#db0000" },
      { value: "contracted_booked", label: "Contracted/Booked", color: "#624E88" },
    ],
    [t]
  );

  const worksheetTypeOptions = useMemo(
    () => [
      { value: "cash", label: t("searchDeals.cashWorksheet") },
      { value: "wholesale", label: t("searchDeals.wholesaleWorksheet") },
      { value: "in_house_finance", label: t("searchDeals.inHouseFinanceWorksheet") },
      { value: "local_lender_finance", label: t("searchDeals.outsideFinanceWorksheet") },
    ],
    [t]
  );

  const defaultLast30 = useMemo(() => resolveDynamicDateRange(DEFAULT_DYNAMIC_DATE_RANGE), []);
  const storedStateRef = useRef(readListState(DEALS_LIST_STATE_KEY));
  const [search, setSearch] = useState(() => storedStateRef.current?.search ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(
    () => (storedStateRef.current?.search ?? "").trim()
  );
  const [status, setStatus] = useState([]);
  const [worksheetTypes, setWorksheetTypes] = useState([]);
  const [dateFilterType, setDateFilterType] = useState(
    () => storedStateRef.current?.dateFilterType ?? "dynamic"
  );
  const [dynamicRange, setDynamicRange] = useState(
    () => storedStateRef.current?.dynamicRange ?? DEFAULT_DYNAMIC_DATE_RANGE
  );
  const [beginningDate, setBeginningDate] = useState(
    () => storedStateRef.current?.beginningDate ?? defaultLast30.startDate
  );
  const [endDate, setEndDate] = useState(
    () => storedStateRef.current?.endDate ?? defaultLast30.endDate
  );
  const [expandedRows, setExpandedRows] = useState({});
  const [page, setPage] = useState(() => storedStateRef.current?.page ?? 1);
  const [pageSize, setPageSize] = useState(() => storedStateRef.current?.pageSize ?? 20);
  const statusInitializedRef = useRef(false);
  const listStateHydratedRef = useRef(false);
  const [filtersReady, setFiltersReady] = useState(false);

  const statusArray = useMemo(
    () => status.map((option) => option.value),
    [status]
  );

  const worksheetTypeArray = useMemo(
    () => worksheetTypes.map((option) => option.value),
    [worksheetTypes]
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);
    return () => clearTimeout(handler);
  }, [search]);

  const prevDebouncedSearchRef = useRef(debouncedSearch);
  useEffect(() => {
    if (prevDebouncedSearchRef.current === debouncedSearch) return;
    prevDebouncedSearchRef.current = debouncedSearch;
    setPage(1);
  }, [debouncedSearch]);

  const { startDate: filterStartDate, endDate: filterEndDate } = useMemo(() => {
    if (dateFilterType === "range") {
      return {
        startDate: beginningDate || undefined,
        endDate: endDate || undefined,
      };
    }
    return resolveDynamicDateRange(dynamicRange || DEFAULT_DYNAMIC_DATE_RANGE);
  }, [dateFilterType, dynamicRange, beginningDate, endDate]);

  useEffect(() => {
    if (statusInitializedRef.current || statusOptions.length === 0) return;

    const stored = storedStateRef.current;
    if (stored?.statusValues?.length) {
      setStatus(statusOptions.filter((opt) => stored.statusValues.includes(opt.value)));
    }
    if (stored?.worksheetTypeValues?.length) {
      setWorksheetTypes(
        worksheetTypeOptions.filter((opt) =>
          stored.worksheetTypeValues.includes(opt.value)
        )
      );
    }
    statusInitializedRef.current = true;
    setFiltersReady(true);
  }, [statusOptions, worksheetTypeOptions]);

  useEffect(() => {
    if (!statusInitializedRef.current) return;

    if (!listStateHydratedRef.current) {
      listStateHydratedRef.current = true;
      return;
    }

    saveListState(DEALS_LIST_STATE_KEY, {
      search,
      statusValues: status.map((option) => option.value),
      worksheetTypeValues: worksheetTypes.map((option) => option.value),
      dateFilterType,
      dynamicRange,
      beginningDate,
      endDate,
      page,
      pageSize,
    });
  }, [
    search,
    status,
    worksheetTypes,
    dateFilterType,
    dynamicRange,
    beginningDate,
    endDate,
    page,
    pageSize,
  ]);

  const handleDateFilterTypeChange = (nextType) => {
    setDateFilterType(nextType);
    setPage(1);
    if (nextType === "range") {
      const range = resolveDynamicDateRange(dynamicRange || DEFAULT_DYNAMIC_DATE_RANGE);
      setBeginningDate(range.startDate);
      setEndDate(range.endDate);
    }
  };

  const handleDynamicRangeChange = (nextRange) => {
    setDynamicRange(nextRange || DEFAULT_DYNAMIC_DATE_RANGE);
    setPage(1);
  };

  const handleBeginningDateChange = (value) => {
    setBeginningDate(value);
    if (value && endDate && value > endDate) {
      setEndDate(value);
    }
    setPage(1);
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
    if (value && beginningDate && value < beginningDate) {
      setBeginningDate(value);
    }
    setPage(1);
  };

  const handleWorksheetTypesChange = (options) => {
    setWorksheetTypes(options || []);
    setPage(1);
  };

  const { data: deals, isLoading } = useGetDeals(
    debouncedSearch,
    statusArray,
    page,
    pageSize,
    filterStartDate,
    filterEndDate,
    worksheetTypeArray,
    filtersReady
  );

  const getDealStatusValue = React.useCallback((row) => row?.deal_status || "", []);

  const statusFilterCounts = useFilterStatusCounts({
    selectedStatuses: status,
    allStatuses: statusOptions,
    results: deals?.results || [],
    getStatusValue: getDealStatusValue,
    enabled: filtersReady && statusOptions.length > 0,
  });

  const serverPageCount = deals?.pagination?.pageCount;
  useEffect(() => {
    if (serverPageCount == null || serverPageCount < 1) return;
    if (page > serverPageCount) {
      setPage(serverPageCount);
    }
  }, [serverPageCount, page]);

  const isTheme2 = shellThemeVariant === "2";
  const DealsTable = isTheme2 ? ReactTable2 : ReactTable;

  const dealColumns = [
    { Header: t("searchDeals.dealId"), accessor: "deal_id", width: 120 },
    { Header: t("searchDeals.receivedDate"), accessor: "received_date", width: 140 },
    {
      Header: t("searchDeals.applicantDetails"),
      accessor: "applicant_details",
      className: "applicant-details-column",
      minWidth: 400,
      Cell: ({ value, row }) => {
        const rowId = row.id;
        const isExpanded = expandedRows[rowId];
        const worksheets = value?.worksheets || [];
        const hasMoreThan3 = worksheets.length > 1;

        return (
          <div>
            {value?.name && (
              <div
                style={{
                  fontWeight: "600",
                  fontSize: "13px",
                  marginBottom: "6px",
                  color: "#1a6fbf",
                }}
              >
                {value.name}
              </div>
            )}
            {worksheets
              .slice(0, isExpanded ? worksheets.length : Math.min(1, worksheets.length))
              .map((worksheet) => (
                <div
                  className="worksheet-container"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "10px",
                    justifyContent: "space-between",
                    gap: "12px",
                    marginBottom: "10px",
                    width: "100%",
                  }}
                  key={worksheet.worksheet_id}
                >
                  <div
                    style={{
                      flex: "1 1 0",
                      minWidth: 0,
                      fontWeight: "bold",
                      fontSize: "13px",
                      textAlign: "left",
                    }}
                  >
                    {worksheet.type === "local_lender_finance"
                      ? worksheet?.finance_company?.name || worksheet?.local_lender?.name
                      : worksheet.type === "cash"
                        ? t("searchDeals.cash")
                        : worksheet.type === "wholesale"
                          ? t("searchDeals.wholesale")
                          : t("searchDeals.inHouse")}
                  </div>
                  <div
                    style={{
                      flex: "1 1 0",
                      minWidth: 0,
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: "13px" }}>
                      {worksheet.inventory?.inventory_vehicle?.year}{" "}
                      {worksheet.inventory?.inventory_vehicle?.make}{" "}
                      {worksheet.inventory?.inventory_vehicle?.model}
                    </div>
                    <div style={{ fontSize: "13px" }}>
                      {worksheet.inventory?.inventory_vehicle?.trim}
                    </div>
                    <div style={{ fontWeight: "bold", fontSize: "12px" }}>
                      {worksheet.contract_date && t("searchDeals.contractDate")}
                    </div>
                    <div style={{ fontSize: "12px" }}>{worksheet?.contract_date}</div>
                    <div style={{ fontSize: "12px" }}>
                      {worksheet?.working_date && t("searchDeals.workingDate")}
                    </div>
                    <div style={{ fontSize: "12px" }}>{worksheet?.working_date}</div>
                  </div>
                  <div
                    style={{
                      flex: "1 1 0",
                      minWidth: 0,
                      textAlign: "right",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "flex-end",
                      gap: "4px",
                    }}
                  >
                    <span style={{ fontSize: "13px", fontWeight: "600", color: "#1a6fbf" }}>
                      {formatDisplayText(worksheet.worksheet_status)}
                    </span>
                    <div style={{ fontSize: "11px", opacity: 0.7 }}>
                      {formatDateForDisplay(worksheet?.status_change_date)}
                    </div>
                  </div>
                </div>
              ))}
            {hasMoreThan3 && (
              <div style={{ position: "relative", zIndex: 1000 }}>
                <Button
                  style={{
                    position: "relative",
                    zIndex: 1001,
                    background: "transparent",
                    border: "none",
                  }}
                  color="link"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedRows((prev) => ({
                      ...prev,
                      [rowId]: !prev[rowId],
                    }));
                  }}
                >
                  <i className={`fas fa-chevron-${isExpanded ? "up" : "down"}`}></i>
                </Button>
              </div>
            )}
          </div>
        );
      },
    },
    { Header: t("searchDeals.createdBy"), accessor: "created_user_full_name", width: 150 },
    {
      Header: () => (
        <div style={{ textAlign: "center", width: "100%" }}>
          {t("searchDeals.status")} / {t("searchDeals.flag")}
        </div>
      ),
      accessor: "status_flag",
      width: 200,
      Cell: ({ value }) => (
        <div
          style={{
            textAlign: "center",
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {value?.status}
          {value?.flag}
        </div>
      ),
    },
  ];

  const dealData = (deals?.results || []).map((item) => ({
    id: item.deal_id,
    deal_id: item.deal_id || "",
    documentId: item.documentId || "",
    deal_status: item.deal_status || "",
    received_date: formatDateForDisplay(resolveDealListReceivedDate(item)),
    applicant_details: {
      name: item?.full_name || item?.wholesaler_name || "",
      worksheets: item?.worksheets || [],
    },
    created_user_full_name: item.created_user_full_name || "",
    status_flag: {
      status: (
        <Badge
          color={
            item.deal_status === "working"
              ? "success"
              : item.deal_status === "prospect"
                ? "info"
                : item.deal_status === "completed"
                  ? "success"
                  : "danger"
          }
        >
          {formatDisplayText(item.deal_status)}
        </Badge>
      ),
      flag: item.is_have_flags ? (
        <Badge color="success">{t("searchDeals.yes")}</Badge>
      ) : (
        <Badge color="danger">{t("searchDeals.no")}</Badge>
      ),
    },
  }));

  const filters = (
    <SearchDealsFilters
      activeColor={activeColor}
      isTheme2={isTheme2}
      search={search}
      onSearchChange={setSearch}
      status={status}
      onStatusChange={setStatus}
      worksheetTypes={worksheetTypes}
      onWorksheetTypesChange={handleWorksheetTypesChange}
      statusOptions={statusOptions}
      worksheetTypeOptions={worksheetTypeOptions}
      dateFilterType={dateFilterType}
      onDateFilterTypeChange={handleDateFilterTypeChange}
      dynamicRange={dynamicRange}
      onDynamicRangeChange={handleDynamicRangeChange}
      beginningDate={beginningDate}
      onBeginningDateChange={handleBeginningDateChange}
      endDate={endDate}
      onEndDateChange={handleEndDateChange}
    />
  );

  const table = (
    <div
      className="inventory-table-wrap"
      style={{ fontSize: "13px", minWidth: 0, maxWidth: "100%" }}
    >
      <DealsTable
        activeColor={activeColor}
        data={dealData}
        columns={dealColumns}
        className={classNames("-striped -highlight", { "sticky-stock-table": isTheme2 })}
        manualPagination={true}
        pageCount={deals?.pagination?.pageCount || 1}
        pageIndex={page - 1}
        totalItems={deals?.pagination?.total}
        pageSize={pageSize}
        noDataText={t("searchDeals.noRecordsFound")}
        loading={isLoading}
        loadingText={t("searchDeals.loading")}
        onPageChange={(pageIndex) => setPage(pageIndex + 1)}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        disableViewportClamp={!isTheme2}
        paginationMetaExtra={<InventoryStatusTicks items={statusFilterCounts} />}
        onRowClick={(row) => {
          if (!row?.deal_id) return;

          if (row.deal_status === "contracted_booked") {
            const worksheets = row?.applicant_details?.worksheets || [];
            const worksheetId =
              worksheets.find((w) => w.worksheet_status === "loan_booked")
                ?.worksheet_id || worksheets[0]?.worksheet_id;

            if (worksheetId) {
              navigate(`/deals/${row.deal_id}?worksheet=${worksheetId}`);
              return;
            }
          }

          navigate(`/deals/${row.deal_id}`);
        }}
      />
    </div>
  );


  return (
    <div
      className={classNames("content", "search-deals", {
        "search-deals--theme2": isTheme2,
        "theme2-admin-content-compact": isTheme2,
        "theme2-search-deals-inset": isTheme2,
      })}
    >
      <SearchDealsCards
        isTheme2={isTheme2}
        filters={filters}
        table={table}
      />
    </div>
  );
};

export default SearchDeals;
